// ===== Widget Type and Config (defined here, re-exported from Dashboard.ts) =====

export type WidgetType = 
  | "dynamic-list" 
  | "static-list" 
  | "shortcut"
  | "subscriptions"
  | "notifications"
  | "favorite-searches"
  | "recent-searches"
  | "favorite-documents";

export type WidgetConfig =
  | DynamicListWidgetConfig
  | StaticListWidgetConfig
  | ShortcutWidgetConfig
  | SubscriptionsWidgetConfig
  | NotificationsWidgetConfig
  | FavoriteSearchesWidgetConfig
  | RecentSearchesWidgetConfig
  | FavoriteDocumentsWidgetConfig;

// ===== Dynamic List Widget Configuration =====

export interface DynamicListWidgetConfig {
  dataSource: "sources" | "workspace-documents";
  filters: DynamicListFilters;
  searchQuery?: string; // Full-text search query (legislation only)
  searchTags?: string[]; // Advanced search tags (legislation only)
  sortBy: string;
  sortAscending: boolean;
  visibleColumns: string[];
  pageSize: number;
}

export interface DynamicListFilters {
  // Selected datasources (kilder)
  datasource?: string[];
  // Filters per datasource - each datasource has its own set of filters
  sourceFilters?: Record<string, Record<string, string[]>>;
  // Workspace-specific filters (supports selecting multiple workspaces)
  workspaceIds?: string[];
  folderId?: string;
  metadataFilters?: Record<string, string[]>;
  dateRange?: {
    field: "CreatedAt" | "UpdatedAt";
    from?: string;
    to?: string;
  };
}

// ===== Static List Widget Configuration =====

/**
 * "mixed" lets a single widget hold both legislation and workspace documents.
 * The other two keep every item in the list to one kind.
 */
export type StaticListDataSource =
  | "sources"
  | "workspace-documents"
  | "mixed";

/** Which kind of document a single static list entry points at. */
export type StaticListItemType = "source" | "workspace-document";

export interface StaticListWidgetConfig {
  dataSource: StaticListDataSource;
  items: StaticListItem[];
  visibleColumns: string[];
}

export interface StaticListItem {
  id: string;
  originalId: string;
  customName?: string;
  sortOrder: number;
  /**
   * Set on every item added since mixed lists shipped. Items saved before that
   * inherit the widget's dataSource — see resolveStaticListItemType.
   */
  itemType?: StaticListItemType;
  // For workspace documents - needed for navigation
  workspaceId?: string;
  workspaceName?: string;
}

// ===== Shortcut Widget Configuration =====

export interface ShortcutWidgetConfig {
  shortcuts: ShortcutItem[];
  layout?: "grid" | "list";
}

export interface ShortcutItem {
  id: string;
  name: string;
  url: string; // Can be external (https://...) or internal (/workspace/...)
  faviconUrl?: string; // Google Favicon API URL for external links
}

// ===== Shortcut Helpers =====

export const getFaviconUrl = (url: string): string | undefined => {
  try {
    const urlObj = new URL(url);
    if (urlObj.protocol === "http:" || urlObj.protocol === "https:") {
      return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=64`;
    }
  } catch {
    // Invalid URL, return undefined
  }
  return undefined;
};

// ===== User Data Widget Configurations (light-configurable) =====

/** Shared base for user-data widgets with optional display order */
export interface UserDataWidgetConfigBase {
  itemOrder?: string[];
  /** When true (default), use global user order from API; when false, itemOrder drives display order */
  useGlobalOrder?: boolean;
}

export type SubscriptionsWidgetConfig = UserDataWidgetConfigBase;

/** No configuration fields — displays notification history */
export type NotificationsWidgetConfig = Record<string, never>;

export type FavoriteDocumentsDataSource =
  | "all"
  | "sources"
  | "workspace-documents";

export interface FavoriteDocumentsWidgetConfig
  extends UserDataWidgetConfigBase {
  dataSource?: FavoriteDocumentsDataSource;
}

export interface FavoriteSearchesWidgetConfig extends UserDataWidgetConfigBase {
  layout?: "grid" | "list";
  maxItems?: number;
}

export interface RecentSearchesWidgetConfig {
  maxItems?: number;
}

// ===== Default Configurations =====

export const getDefaultDynamicListConfig = (): DynamicListWidgetConfig => ({
  dataSource: "sources",
  filters: {},
  searchQuery: "",
  sortBy: "published_on",
  sortAscending: false,
  visibleColumns: ["title", "type", "date", "status"],
  pageSize: 10,
});

export const getDefaultStaticListConfig = (): StaticListWidgetConfig => ({
  dataSource: "sources",
  items: [],
  visibleColumns: ["title", "type", "date", "status"],
});

export const getDefaultShortcutConfig = (): ShortcutWidgetConfig => ({
  shortcuts: [],
  layout: "grid",
});

export const getDefaultSubscriptionsConfig = (): SubscriptionsWidgetConfig => ({
  useGlobalOrder: true,
});

export const getDefaultNotificationsConfig = (): NotificationsWidgetConfig => ({});

export const getDefaultFavoriteSearchesConfig = (): FavoriteSearchesWidgetConfig => ({
  useGlobalOrder: true,
  layout: "grid",
});

export const getDefaultRecentSearchesConfig = (): RecentSearchesWidgetConfig => ({});

export const getDefaultFavoriteDocumentsConfig =
  (): FavoriteDocumentsWidgetConfig => ({
    useGlobalOrder: true,
    dataSource: "all",
  });

// ===== Type Guards =====

export const isDynamicListConfig = (
  config: unknown
): config is DynamicListWidgetConfig => {
  return (
    typeof config === "object" &&
    config !== null &&
    "dataSource" in config &&
    "filters" in config &&
    "sortBy" in config
  );
};

export const isStaticListConfig = (
  config: unknown
): config is StaticListWidgetConfig => {
  return (
    typeof config === "object" &&
    config !== null &&
    "dataSource" in config &&
    "items" in config &&
    Array.isArray((config as StaticListWidgetConfig).items)
  );
};

export const isShortcutConfig = (
  config: unknown
): config is ShortcutWidgetConfig => {
  return (
    typeof config === "object" &&
    config !== null &&
    "shortcuts" in config &&
    Array.isArray((config as ShortcutWidgetConfig).shortcuts)
  );
};

// Type guards for self-contained widgets (they accept any object or empty object)
export const isSubscriptionsConfig = (
  config: unknown
): config is SubscriptionsWidgetConfig => {
  return typeof config === "object" && config !== null;
};

export const isNotificationsConfig = (
  config: unknown
): config is NotificationsWidgetConfig => {
  return typeof config === "object" && config !== null;
};

export const isFavoriteSearchesConfig = (
  config: unknown
): config is FavoriteSearchesWidgetConfig => {
  return typeof config === "object" && config !== null;
};

export const isRecentSearchesConfig = (
  config: unknown
): config is RecentSearchesWidgetConfig => {
  return typeof config === "object" && config !== null;
};

export const isFavoriteDocumentsConfig = (
  config: unknown
): config is FavoriteDocumentsWidgetConfig => {
  return typeof config === "object" && config !== null;
};

// ===== Column Definitions =====

export interface ColumnDefinition {
  id: string;
  label: string;
  defaultVisible: boolean;
}

export const SOURCES_COLUMNS: ColumnDefinition[] = [
  { id: "title", label: "Titel", defaultVisible: true },
  { id: "type", label: "Type", defaultVisible: true },
  { id: "datasource", label: "Kilde", defaultVisible: true },
  { id: "date", label: "Dato", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
];

/**
 * Columns for a mixed list. Every column has to mean something for both kinds
 * of row, so `source` folds together the sources `datasource` column and the
 * workspace `workspace` column, and `date` folds together `date` and
 * `updatedAt`. Status is deliberately absent: "Gældende" (legal validity) and
 * "Indekseret" (processing state) are unrelated facts that read as noise when
 * they share a column.
 */
export const MIXED_COLUMNS: ColumnDefinition[] = [
  { id: "origin", label: "Oprindelse", defaultVisible: true },
  { id: "title", label: "Titel", defaultVisible: true },
  { id: "type", label: "Type", defaultVisible: true },
  { id: "source", label: "Kilde/Workspace", defaultVisible: true },
  { id: "date", label: "Dato", defaultVisible: true },
];

export const WORKSPACE_DOCUMENT_COLUMNS: ColumnDefinition[] = [
  { id: "name", label: "Navn", defaultVisible: true },
  { id: "workspace", label: "Workspace", defaultVisible: true },
  { id: "type", label: "Type", defaultVisible: true },
  { id: "updatedAt", label: "Opdateret", defaultVisible: true },
  { id: "createdAt", label: "Oprettet", defaultVisible: false },
];
